import { formatTime } from "./formatTime";
import type { Job } from "../types";

/**
 * Formats a job date + time into an iCal datetime string (YYYYMMDDTHHMMSS).
 */
function toICalDate(dateStr: string, timeStr: string) {
  const [y, m, d] = dateStr.split("-");
  const [h, min] = timeStr.split(":");
  return `${y}${m}${d}T${h}${min}00`;
}

/**
 * Returns a UTC timestamp string in iCal format for use as DTSTAMP.
 */
function nowICalStamp() {
  const d = new Date();
  return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

/**
 * Builds the description text for a calendar event from a job.
 */
function buildDescription(job: Job) {
  const lines: string[] = [];
  if (job.entity) lines.push(`Entity: ${job.entity}`);
  lines.push(`Pay: $${job.payRate}/hr`);
  lines.push(`Slots: ${(job.signups || []).length}/${job.slotsTotal}`);
  if (job.pointOfContact) {
    if (typeof job.pointOfContact === "string") {
      lines.push(`Contact: ${job.pointOfContact}`);
    } else if (Array.isArray(job.pointOfContact)) {
      (job.pointOfContact as Array<{ name?: string; phone?: string; email?: string }>)
        .filter((c) => c.name || c.phone || c.email)
        .forEach((c, i, arr) => {
          const parts = [c.name, c.phone, c.email].filter(Boolean).join(" | ");
          lines.push(`Contact${arr.length > 1 ? ` ${i + 1}` : ""}: ${parts}`);
        });
    }
  }
  if (job.details) lines.push(`\n${job.details}`);
  return lines.join("\n");
}

/**
 * Escapes text for iCal format (folds long lines, escapes special chars).
 */
function icalEscape(text: string) {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

/**
 * Generates an ICS file string for one or more jobs.
 */
function generateICS(jobs: Job[]) {
  const events = jobs.map((job) => {
    const uid = `${job.id}@officeronduty`;
    const dtStart = toICalDate(job.date, job.startTime);
    const dtEnd = toICalDate(job.date, job.endTime);
    const summary = icalEscape(job.title);
    const location = icalEscape(job.location || "");
    const description = icalEscape(buildDescription(job));

    return [
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${nowICalStamp()}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      `SUMMARY:${summary}`,
      `LOCATION:${location}`,
      `DESCRIPTION:${description}`,
      "STATUS:CONFIRMED",
      "BEGIN:VALARM", "TRIGGER:-P1D", "ACTION:DISPLAY",
      `DESCRIPTION:Reminder: ${summary}`, "END:VALARM",
      "BEGIN:VALARM", "TRIGGER:-PT1H", "ACTION:DISPLAY",
      `DESCRIPTION:Reminder: ${summary}`, "END:VALARM",
      "END:VEVENT",
    ].join("\r\n");
  });

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//OfficerOnDuty//Jobs//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    ...events,
    "END:VCALENDAR",
  ].join("\r\n");
}

/**
 * Downloads an .ics file for one or more jobs.
 * Works on desktop and mobile (iPhone opens in Calendar app, Android in Google Calendar).
 */
export function downloadICS(jobs: Job | Job[]) {
  const jobsArray = Array.isArray(jobs) ? jobs : [jobs];
  const ics = generateICS(jobsArray);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = jobsArray.length === 1 ? `${jobsArray[0].title.replace(/[^a-zA-Z0-9]/g, "_")}.ics` : "jobs.ics";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Opens Google Calendar with a pre-filled event for a single job.
 */
export function openGoogleCalendar(job: Job) {
  const start = toICalDate(job.date, job.startTime);
  const end = toICalDate(job.date, job.endTime);
  const description = buildDescription(job);

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: job.title,
    dates: `${start}/${end}`,
    location: job.location || "",
    details: description,
  });

  window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, "_blank");
}

/**
 * Opens Outlook.com calendar with a pre-filled event for a single job.
 */
export function openOutlookCalendar(job: Job) {
  const [y, m, d] = job.date.split("-");
  const startISO = `${y}-${m}-${d}T${job.startTime}:00`;
  const endISO = `${y}-${m}-${d}T${job.endTime}:00`;
  const description = buildDescription(job);

  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: job.title,
    startdt: startISO,
    enddt: endISO,
    location: job.location || "",
    body: description,
  });

  window.open(`https://outlook.live.com/calendar/0/action/compose?${params.toString()}`, "_blank");
}

/**
 * Returns the human-readable time range for display.
 */
export function getTimeRange(job: Job) {
  return `${formatTime(job.startTime)} – ${formatTime(job.endTime)}`;
}
